/**
 *
 * Param channel sync middleware
 *
 * ⚠️ Reads POST-reducer state, never the payload, so the write rule (prune-at-initial, `canonical`, `quantize`) and morph handling reach the channel without being reimplemented.
 *
 * ⚠️ SCOPED TO WHAT THE ACTION NAMED. Redux holds PRE-fade values for the whole sweep — a fade writes only the channel — so a full reconcile on a settled edit yanks every other param back to where the fade started and flattens a morph envelope to a string. Only a whole-state replacement reconciles everything.
 *
 */
import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit'
import paramChannel from '~/lib/paramChannel'
import { EFFECT_SCHEMAS } from '~/schemas/effect_schemas'
import {
	patchLiveObject,
	resetLiveObject,
	setLiveObject,
	setLiveProperties,
	setLiveProperty,
	updateModulator,
} from '~/store/live'
import { selectLive } from '~/store/live/selectors'

const EFFECT_NAMES = Object.keys(EFFECT_SCHEMAS)

/**
 * ⚠️ Reconciles both ways — iterating the present section alone only ever adds, and absence is how the sparse model spells at-initial.
 *
 * `only` narrows the reconcile to the keys an action actually named. A patch touches SOME keys of a section; Redux holds pre-fade values for the rest, so reconciling the whole section would yank a mid-fade sibling back to where its fade started. Omit it for a whole-state replacement, which legitimately owns every key.
 */
const syncEffect = (live, effect, only) => {
	const section = live[effect]
	const present = section && typeof section === 'object' ? section : null
	for (const key of paramChannel.keys(effect)) {
		if (only && !only.has(key)) continue
		if (!present || !(key in present)) paramChannel.delete(`${effect}.${key}`)
	}
	if (!present) return
	for (const param in present) {
		if (only && !only.has(param)) continue
		paramChannel.set(`${effect}.${param}`, present[param])
	}
}

/**
 * Mirror one dot-path, read through `live` so the value is the pruned one.
 *
 * ⚠️ A path INSIDE a param (`diffusion.advanced.detail_view`) mirrors that whole param: the channel's keys are effect params, `advanced` is one of them whose value is a dict, and the wire sender reads the section from the channel — skipping the write left it stale and the edit went out one write late.
 */
const syncKey = (live, path) => {
	const dot = path.indexOf('.')
	if (dot <= 0) return
	const effect = path.slice(0, dot)
	if (!EFFECT_SCHEMAS[effect]) return
	const param = path.slice(dot + 1).split('.')[0]
	const section = live[effect]
	const value = section && typeof section === 'object' ? section[param] : undefined
	const key = `${effect}.${param}`
	if (value === undefined) paramChannel.delete(key)
	else paramChannel.set(key, value)
}

/** `[effect, keysTouched]` for every effect section the patch names. The key set is what scopes the reconcile — a patch speaks only for what it carries. */
const patchedEffects = payload => {
	const out = []
	if (!payload || typeof payload !== 'object') return out
	for (const effect in payload) {
		if (!EFFECT_SCHEMAS[effect]) continue
		const section = payload[effect]
		// A non-object section (or an explicit removal) names no keys, so the whole section is its scope.
		out.push([effect, section && typeof section === 'object' ? new Set(Object.keys(section)) : null])
	}
	return out
}

const paramChannelSync = createListenerMiddleware()

// A preset load, undo/redo or reset: everything the channel holds is stale, so this is the one full reconcile.
paramChannelSync.startListening({
	matcher: isAnyOf(setLiveObject, resetLiveObject),
	effect: async (_action, { getState }) => {
		const live = selectLive(getState())
		for (const effect of EFFECT_NAMES) syncEffect(live, effect)
	},
})

paramChannelSync.startListening({
	matcher: isAnyOf(setLiveProperty, setLiveProperties, updateModulator),
	effect: async (action, { getState }) => {
		const live = selectLive(getState())
		const { type, payload } = action
		if (type === setLiveProperty.type) syncKey(live, payload[0])
		else if (type === setLiveProperties.type) for (const [k] of payload) syncKey(live, k)
		else if (payload.value !== undefined) syncKey(live, payload.path)
	},
})

paramChannelSync.startListening({
	matcher: patchLiveObject,
	effect: async (action, { getState }) => {
		const live = selectLive(getState())
		for (const [effect, keys] of patchedEffects(action.payload)) syncEffect(live, effect, keys)
	},
})

export default paramChannelSync
